import { useState, useEffect } from "react";
import { fetchMonthlyTrend } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Monthly Production Trend chart filter panel
 * on ProductionMilestonesPage — the sparse daily SQFT/personnel series for
 * one selected calendar month, plus its summary stats.
 *
 * ── Trigger contract ──────────────────────────────────────────────────────
 *
 * Re-fetches whenever `appliedYear` or `appliedMonthIdx` change. The page
 * already owns the Apply-button flow (handleApplyTrend commits draft
 * `trendYear` / `trendMonth` → `appliedTrendYear` / `appliedTrendMonth` only
 * on click) — this hook just reacts to those committed values.
 *
 * ── No modal deep-link path ────────────────────────────────────────────────
 *
 * Unlike Weekly Rolls, there is no ModRollTime-style modal that deep-links
 * into a specific month — appliedTrendYear/appliedTrendMonth are always
 * initialised from `new Date()` on the page (today's year/month), and this
 * hook fires on mount with that default exactly like useProductionMilestones
 * does for the KPI cards. No special first-render resolution is needed here.
 *
 * ── Why this hook stays separate from useProductionMilestones ─────────────
 *
 * production-milestones (the page-load route) bundles a default trend chart
 * for the CURRENT month alongside the 5 KPIs, so the chart isn't empty on
 * first paint. But once the user picks a different year/month and clicks
 * Apply, the chart needs to reload independently of the KPI cards — those
 * shouldn't re-fetch just because someone is browsing trend history. This
 * hook's first fetch (for the current month) will return the same data the
 * page-load bundle already has, which is a deliberate, cheap redundancy in
 * exchange for keeping the two concerns fully decoupled; see note in
 * ProductionMilestonesPage integration (output 4) for how the two are
 * reconciled on initial render.
 *
 * @param {number} appliedYear      Full calendar year (e.g. 2026)
 * @param {number} appliedMonthIdx  0-based month index (0 = Jan … 11 = Dec)
 *
 * @returns {{
 *   month: string,
 *   days: Array<{ day: number, sqft: number, personnel: number }>,
 *   stats: { avgSqft: number, peakSqft: number, peakDay: number, avgPers: number } | null,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

const IDLE_STATE = {
  month: "",
  days: [],
  stats: null,
};

const useMonthlyTrend = (appliedYear, appliedMonthIdx) => {
  const [data, setData] = useState(IDLE_STATE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    // Guard against an uninitialised caller (e.g. page hasn't resolved its
    // default year/month yet). Both values are required and validated again
    // inside fetchMonthlyTrend, but failing fast here avoids a doomed request.
    if (!Number.isFinite(appliedYear) || !Number.isFinite(appliedMonthIdx)) {
      setData(IDLE_STATE);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchMonthlyTrend(
          { year: appliedYear, monthIdx: appliedMonthIdx },
          controller.signal,
        );
        setData(result);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load monthly trend data.");
        setData(IDLE_STATE);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [appliedYear, appliedMonthIdx]);

  return { ...data, loading, error };
};

export default useMonthlyTrend;
